<script setup lang="ts">
  import { useFlightStore } from '@/components/fsf/stores/flight.store'
  import type { IRadioItem } from '@/components/ui/radio/types'
  import { useFsfStore } from '@/components/fsf/stores/fsf.store'
  import { announce } from '@/utils/a11yAlert'
  import MonthMenu from '@/components/fsf/MonthMenu.vue'
  import { ONEWAY, RETURN } from '@/utils/constants/fsf'
  import { useGlobalSettingsStore } from '@/stores/GlobalSettings.store'

  const globalSettingsStore = useGlobalSettingsStore()
  const { ripStatus } = storeToRefs(globalSettingsStore)
  if (ripStatus.value?.flt) {
    await navigateTo('/')
  }

  const { isDesktop } = useBTBreakpoints()

  definePageMeta({
    layout: 'fsf',
    middleware: 'fsf-data-handler',
    disableCobrowseForMobileApp: true
  })

  const currentLayout = computed(() => (isDesktop.value ? 'fsf-desktop' : 'fsf-mobile'))

  const { t } = useI18n()

  useSeoMeta({
    title: t('fsf.meta.title'),
    description: t('fsf.meta.description'),
    ogSiteName: 'airBaltic.com'
  })

  useHead({
    titleTemplate: '%s | airBaltic'
  })

  const flightModes: IRadioItem[] = [
    {
      value: RETURN,
      label: t('fsf.roundTrip')
    },
    {
      value: ONEWAY,
      label: t('fsf.oneWay')
    }
  ]

  const fsfStore = useFsfStore()
  const flightStore = useFlightStore()

  watchEffect(
    () => {
      if (flightStore.isFlightDataPending) {
        announce(t('fsf.calendar.loadingData'), true)
      } else {
        announce(t('fsf.calendar.dataLoaded'), true)
      }
    },
    { flush: 'post' }
  )

  const monthMenuKeyIndex = ref(0)

  const changeTripType = () => {
    fsfStore.$reset()
    monthMenuKeyIndex.value++
  }

  const monthCalendarKey = computed(() => {
    return `${fsfStore.currentFlightMode}-${monthMenuKeyIndex.value}`
  })
</script>
<template>
  <h1 class="sr-only">
    {{ $t('fsf.hiddenH1') }}
  </h1>
  <FsfOverlay v-if="flightStore.isFlightDataPending" />
  <ClientOnly>
    <template #fallback>
      <FsfOverlay />
    </template>
    <NuxtLayout :name="currentLayout">
      <template
        v-if="isDesktop || fsfStore.currentFlightMode !== ONEWAY"
        #tab-row
      >
        <FsfTabRow />
      </template>
      <template #trip-type>
        <UiRadioGroup
          v-model="fsfStore.currentFlightMode"
          as="section"
          class="!gap-x-6"
          :items="flightModes"
          direction="row"
          :aria-label="$t('fsf.tripType')"
          @update:model-value="changeTripType"
        />
      </template>
      <template
        v-if="t('fsf.disclaimer') !== ''"
        #switch
      >
        <UiAlert
          variant="warning"
          :closable="true"
          :text="t('fsf.disclaimer')"
        />
      </template>
      <template #month-menu>
        <MonthMenu
          :key="monthCalendarKey"
          v-model="fsfStore.selectedMonth"
        />
      </template>
      <template #calendar>
        <FsfCalendar />
      </template>
      <template #footer>
        <FsfFooter />
      </template>
      <template #legends>
        <FsfCalendarLegends />
      </template>
    </NuxtLayout>
  </ClientOnly>
</template>
